import { useCallback, useMemo } from "react";
// constants
import {
    DEFAULT_FREQUENCY_CAP_VALUE,
    DEFAULT_FREQUENCY_LIMIT,
    DISABLE_FREQUENCY_CAPPING,
    SessionVisibility,
    ProceedMode,
} from "./constants";
// types
import { LocalStorageDataForFrequency } from "@checkout-core/interfaces/frequencyCapping";
// types
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-types";
// utils
import { GlobalHeaderState } from "@checkout-core/utils";
import { isEmpty } from "@swiggy-private/js-utils";
// deps
import { LocalStorage } from "@swiggy-private/core-ui";

export interface FrequencyCapProps {
    key: string; // Key to be used for local storage
    id?: string; // unique identifier if we need to track frequency based on id
    maxFrequency?: number;
    sessionVisibility?: SessionVisibility;
    sessionId?: string;
    disableMaxFrequency?: boolean;
}

interface ShouldProceedOptions {
    mode?: ProceedMode;
}

/**
 * Custom hook for implementing frequency capping based on local storage.
 *
 * @param {FrequencyCapProps} options - The options for frequency capping.
 * @param {string} options.id - The ID for the frequency capping.
 * @param {string} options.key - The key for storing the frequency capping data in local storage.
 * @param {number} [options.maxFrequency=DEFAULT_FREQUENCY_LIMIT] - The maximum frequency allowed.
 * @param {SessionVisibility} [options.sessionVisibility=SessionVisibility.EVERYTIME_PER_SESSION] - The session visibility for the frequency capping.
 * @param {string} [options.sessionId] - The session ID for the frequency capping.
 * @returns {Object} - An object containing the `shouldProceed` function.
 */
export const useLocalStorageBasedFrequencyCapping = ({
    id,
    key,
    maxFrequency = DEFAULT_FREQUENCY_LIMIT,
    sessionVisibility = SessionVisibility.EVERYTIME_PER_SESSION,
    sessionId,
    disableMaxFrequency = false,
}: FrequencyCapProps) => {
    // For IM, we won't receive the sessionId from the header, so we'll retrieve it from the props instead.
    const currentSid = useMemo(() => {
        if (isEmpty(sessionId)) {
            return GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "0";
        }
        return sessionId;
    }, [sessionId]);

    const checkSessionIdAndFrequency = useCallback(
        (key: string, currentSid: string, updateStorage: boolean) => {
            try {
                const savedData = LocalStorage.getItem(
                    key,
                    DEFAULT_FREQUENCY_CAP_VALUE,
                ) as LocalStorageDataForFrequency;

                if (isEmpty(savedData?.id) || isEmpty(id)) {
                    delete savedData.id;
                }

                const { counterValue, sessionId } = savedData;

                if (!isEmpty(sessionId) && sessionId === currentSid) {
                    return sessionVisibility === SessionVisibility.EVERYTIME_PER_SESSION;
                } else if (!disableMaxFrequency && counterValue < maxFrequency) {
                    // Update frequency only if updateStorage is true
                    if (updateStorage) {
                        const updatedSessionData = {
                            ...savedData,
                            counterValue: counterValue + 1,
                            sessionId: currentSid,
                        };
                        localStorage.setItem(key, JSON.stringify(updatedSessionData));
                    }
                    return true;
                } else if (disableMaxFrequency) {
                    // Update frequency only if key exist
                    if (updateStorage) {
                        const updatedSessionData = {
                            ...savedData,
                            counterValue: counterValue,
                            sessionId: currentSid,
                        };
                        localStorage.setItem(key, JSON.stringify(updatedSessionData));
                    }
                    return true;
                }

                // Frequency limit exceeded, return false
                return false;
            } catch {
                return false;
            }
        },
        [id, maxFrequency, sessionVisibility, disableMaxFrequency],
    );

    const shouldProceed = useCallback(
        ({ mode = ProceedMode.CHECK_AND_UPDATE }: ShouldProceedOptions = {}) => {
            try {
                if (!isEmpty(id) && id === DISABLE_FREQUENCY_CAPPING) {
                    return true;
                }
                const savedData = LocalStorage.getItem(key, DEFAULT_FREQUENCY_CAP_VALUE);

                if (!isEmpty(id) && (isEmpty(savedData?.id) || savedData?.id !== id)) {
                    if (mode === ProceedMode.ONLY_CHECK) {
                        return true;
                    }

                    if (mode === ProceedMode.CHECK_AND_UPDATE) {
                        const updatedData = { ...DEFAULT_FREQUENCY_CAP_VALUE, id };
                        LocalStorage.setItem(key, updatedData);
                    }
                }

                // Call function with key as parameter and return the boolean result
                return checkSessionIdAndFrequency(key, currentSid, mode === ProceedMode.CHECK_AND_UPDATE);
            } catch {
                return false;
            }
        },
        [checkSessionIdAndFrequency, currentSid, id, key],
    );

    const isSessionValid = (sessionKey: string = key) => {
        try {
            const storedSid = localStorage.getItem(sessionKey);
            if (isEmpty(storedSid) || storedSid !== currentSid) {
                localStorage.setItem(sessionKey, currentSid);
                return true;
            }

            return false;
        } catch {
            return false;
        }
    };

    return { shouldProceed, isSessionValid };
};
